import {
    ContentCopyRounded,
    DeleteOutlineRounded,
    EventAvailableRounded,
    EventBusyRounded,
    HowToRegRounded,
    KeyboardDoubleArrowRightRounded,
} from '@mui/icons-material';
import { CircularProgress, Divider, ListItemIcon, Menu, MenuItem, Tooltip } from '@mui/material';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PostDuplicateDialog from '../post/actions/PostDuplicateDialog';
import PostDeleteDialog from '../post/actions/PostDeleteDialog';
import { ArrayElement } from '@/utils/types/array-element';
import { PostsQuery } from '@/graphql/generated/posts.generated';
import { SchedulePostDocument } from '@/graphql/generated/schedule-post.generated';
import { enqueueSnackbar } from 'notistack';
import { usePostActionError } from '@/components/billing/XCreditsDialog';
import { useMutation, useQuery } from '@apollo/client';
import { TeamPermission } from '@/graphql/generated/types.generated';
import { UnschedulePostDocument } from '@/graphql/generated/unschedule-post.generated';
import dayjs from 'dayjs';
import { TeamReviewPostDocument } from '@/graphql/generated/team-review-post.generated';
import { TeamPermissionsDocument } from '@/graphql/generated/team-permissions.generated';
import { usePosts } from '../providers/PostsProvider';
import {
    getPostActionPolicy,
    hasPostMenuActions,
} from '@/components/post-planning/utils/post-access-policy.util';

type PostMenuProps = {
    children: React.ReactNode;
    post: ArrayElement<PostsQuery['posts']['items']>;
    publicView?: boolean;
    timezone?: string;
};
const PostMenuContent = forwardRef<HTMLDivElement, Omit<PostMenuProps, 'publicView'>>(
    ({ children, post, timezone }, ref) => {
        const { t } = useTranslation();
        const onPostActionError = usePostActionError();
        const { updatePost } = usePosts();
        const policy = getPostActionPolicy(post);

        const [contextMenu, setContextMenu] = useState<{
            mouseX: number;
            mouseY: number;
        } | null>(null);
        const [openDuplicate, setOpenDuplicate] = useState(false);
        const [openDelete, setOpenDelete] = useState(false);

        const handleContextMenu = (event: React.MouseEvent) => {
            if (!hasPostMenuActions(policy)) {
                return;
            }
            event.preventDefault();
            setContextMenu(
                contextMenu === null
                    ? {
                          mouseX: event.clientX + 2,
                          mouseY: event.clientY - 6,
                      }
                    : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
                      // Other native context menus might behave different.
                      // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
                      null,
            );
        };

        const handleClose = () => {
            setContextMenu(null);
        };

        const [isSchedule, setIsSchedule] = useState(false);
        const [schedulePost, { loading: scheduling }] = useMutation(SchedulePostDocument, {
            onCompleted: ({ schedulePost }) => {
                updatePost(schedulePost);
                enqueueSnackbar(
                    t('snackbar.content.scheduled', {
                        date: dayjs(schedulePost.publishAt).tz(timezone).format('L LT'),
                    }),
                );
                handleClose();
            },
            onError: onPostActionError,
        });

        const [unschedulePost, { loading: unscheduling }] = useMutation(UnschedulePostDocument, {
            onCompleted: ({ unschedulePost }) => {
                updatePost(unschedulePost);
                enqueueSnackbar(t('snackbar.content.unscheduled'));
                handleClose();
            },
            onError: onPostActionError,
        });

        const { data, loading: gettingTeamPerms } = useQuery(TeamPermissionsDocument);

        const [teamReview, { loading: sendingForReview }] = useMutation(TeamReviewPostDocument, {
            onCompleted: ({ teamReviewPost }) => {
                updatePost(teamReviewPost);
                enqueueSnackbar('post-review');
            },
            onError: onPostActionError,
        });

        const loading = scheduling || unscheduling || sendingForReview || gettingTeamPerms;

        const canSchedule = policy.schedule;
        const canUnschedule = policy.unschedule;
        const canPublishNow = policy.publishNow;
        const canPublish = data?.teamPermissions.includes(TeamPermission.PublishPost);
        const canSendForReview = policy.sendForReview;
        const canDelete = policy.delete;
        const canDuplicate = policy.duplicate;

        return (
            <div onContextMenu={handleContextMenu} style={{ width: '100%' }} ref={ref}>
                <Menu
                    open={contextMenu !== null}
                    onClose={handleClose}
                    anchorReference="anchorPosition"
                    anchorPosition={
                        contextMenu !== null
                            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                            : undefined
                    }
                    slotProps={{
                        paper: { elevation: 8, sx: { borderRadius: 2 } },
                    }}
                    keepMounted
                >
                    {canDuplicate && (
                        <PostDuplicateDialog
                            postId={post.id}
                            open={openDuplicate}
                            onCancel={() => setOpenDuplicate(false)}
                        >
                            <MenuItem
                                disabled={loading}
                                onClick={() => {
                                    setOpenDuplicate(true);
                                    handleClose();
                                }}
                            >
                                <ListItemIcon>
                                    <ContentCopyRounded />
                                </ListItemIcon>
                                {t('str.action.duplicate')}
                            </MenuItem>
                        </PostDuplicateDialog>
                    )}

                    {/* <MenuItem onClick={handleClose}>
                    <ListItemIcon>
                        <CheckCircleOutlineRounded />
                    </ListItemIcon>
                    {t('str.action.request.team-review')}
                </MenuItem>
                <MenuItem onClick={handleClose}>
                    <ListItemIcon>
                        <MarkEmailReadRounded />
                    </ListItemIcon>
                    {t('str.action.request.client-review')}
                </MenuItem> */}
                    {(canSchedule || canUnschedule || canPublishNow || canSendForReview) && <Divider />}
                    {canSchedule && (
                        <Tooltip title={!canPublish ? t('str.no-publish-permission') : null}>
                            <div>
                                <MenuItem
                                    onClick={() => {
                                        setIsSchedule(true);
                                        schedulePost({ variables: { postId: post.id } });
                                    }}
                                    disabled={!post.publishAt || loading || !canPublish}
                                >
                                    <ListItemIcon>
                                        {scheduling && isSchedule ? (
                                            <CircularProgress color="inherit" size={20} />
                                        ) : (
                                            <EventAvailableRounded />
                                        )}
                                    </ListItemIcon>
                                    {t('str.action.schedule')}
                                </MenuItem>
                            </div>
                        </Tooltip>
                    )}
                    {canUnschedule && (
                        <MenuItem
                            onClick={() => {
                                setIsSchedule(true);
                                unschedulePost({ variables: { postId: post.id } });
                            }}
                            disabled={loading}
                        >
                            <ListItemIcon>
                                {unscheduling ? (
                                    <CircularProgress color="inherit" size={20} />
                                ) : (
                                    <EventBusyRounded />
                                )}
                            </ListItemIcon>
                            {t('post.action.unschedule')}
                        </MenuItem>
                    )}

                    {canPublishNow && (
                        <Tooltip title={!canPublish ? t('str.no-publish-permission') : null}>
                            <div>
                                <MenuItem
                                    onClick={() => {
                                        setIsSchedule(false);
                                        schedulePost({ variables: { postId: post.id, publishNow: true } });
                                    }}
                                    disabled={loading || !canPublish}
                                >
                                    <ListItemIcon>
                                        {scheduling && !isSchedule ? (
                                            <CircularProgress color="inherit" size={20} />
                                        ) : (
                                            <KeyboardDoubleArrowRightRounded />
                                        )}
                                    </ListItemIcon>
                                    {t('str.action.publish-now')}
                                </MenuItem>
                            </div>
                        </Tooltip>
                    )}
                    {canSendForReview && (
                        <MenuItem
                            onClick={() => {
                                teamReview({ variables: { postId: post.id } });
                            }}
                            disabled={loading}
                        >
                            <ListItemIcon>
                                {sendingForReview ? (
                                    <CircularProgress color="inherit" size={20} />
                                ) : (
                                    <HowToRegRounded />
                                )}
                            </ListItemIcon>
                            {t('str.send-for-review')}
                        </MenuItem>
                    )}
                    {canDelete && (
                        <div>
                            <Divider sx={{ mb: 0.75 }} />
                            <PostDeleteDialog
                                postId={post.id}
                                open={openDelete}
                                onCancel={() => setOpenDelete(false)}
                                onDelete={() => setOpenDelete(false)}
                            >
                                <MenuItem
                                    disabled={loading}
                                    onClick={() => {
                                        setOpenDelete(true);
                                        handleClose();
                                    }}
                                    sx={{ color: 'error.main' }}
                                >
                                    <ListItemIcon>
                                        <DeleteOutlineRounded color="error" />
                                    </ListItemIcon>
                                    {t('str.action.escape.delete')}
                                </MenuItem>
                            </PostDeleteDialog>
                        </div>
                    )}
                </Menu>
                {children}
            </div>
        );
    },
);

const PostMenu = forwardRef<HTMLDivElement, PostMenuProps>(
    ({ children, post, publicView, timezone }, ref) => {
        if (publicView) {
            return <div style={{ width: '100%' }}>{children}</div>;
        }

        return (
            <PostMenuContent post={post} ref={ref} timezone={timezone}>
                {children}
            </PostMenuContent>
        );
    },
);

export default PostMenu;
